import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import './Roadmap.css';

const Roadmap = () => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const roadmapItems = [
    {
      quarter: 'Q1 2024',
      title: 'Foundation',
      status: 'completed',
      items: [
        'Token Launch & Smart Contract Deployment',
        'Comprehensive Security Audit',
        'Community Building & Social Channels',
        'Initial Exchange Listings'
      ]
    },
    {
      quarter: 'Q2 2024',
      title: 'Expansion',
      status: 'active',
      items: [
        'Major DEX Listings & Liquidity Pools',
        'Staking Platform Launch',
        'Mobile App Beta Release',
        'Strategic Partnerships'
      ]
    },
    {
      quarter: 'Q3 2024',
      title: 'Innovation',
      status: 'upcoming',
      items: [
        'NFT Marketplace Launch',
        'Cross-chain Bridge Implementation',
        'Governance Platform & DAO Formation',
        'DeFi Protocol Integrations'
      ]
    },
    {
      quarter: 'Q4 2024',
      title: 'Ecosystem',
      status: 'upcoming',
      items: [
        'Advanced DeFi Protocols',
        'Enterprise Solutions & APIs',
        'Global Marketing Campaign',
        'Layer 2 Scaling Solutions'
      ]
    },
    {
      quarter: 'Q1 2025',
      title: 'Future',
      status: 'planned',
      items: [
        'Institutional Partnerships',
        'AI-Powered Trading Tools',
        'Metaverse Integration',
        'Quantum-Resistant Upgrades'
      ]
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section id="roadmap" className="roadmap" ref={ref}>
      <div className="container">
        <motion.div 
          className="section-header"
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2 className="section-title">Roadmap</h2>
          <p className="section-subtitle">Our journey to revolutionize decentralized finance</p>
        </motion.div>

        <motion.div 
          className="roadmap-timeline"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {roadmapItems.map((item, index) => (
            <motion.div
              key={index}
              className={`timeline-item ${item.status}`}
              variants={itemVariants}
              whileHover={{ scale: 1.02 }}
            >
              <motion.div 
                className="timeline-marker"
                whileHover={{ scale: 1.2 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                {item.status === 'completed' && (
                  <motion.svg 
                    width="24" 
                    height="24" 
                    viewBox="0 0 24 24" 
                    fill="none"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: index * 0.2 + 0.5 }}
                  >
                    <path 
                      d="M20 6L9 17L4 12" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </motion.svg>
                )}
                {item.status === 'active' && (
                  <motion.div 
                    className="active-pulse"
                    animate={{ 
                      scale: [1, 1.5, 1],
                      opacity: [1, 0.5, 1]
                    }}
                    transition={{ 
                      duration: 2, 
                      repeat: Infinity 
                    }}
                  />
                )}
                {(item.status === 'upcoming' || item.status === 'planned') && (
                  <motion.div 
                    className="upcoming-dot"
                    animate={{ rotate: 360 }}
                    transition={{ 
                      duration: 4, 
                      repeat: Infinity,
                      ease: "linear"
                    }}
                  />
                )}
              </motion.div>

              <motion.div 
                className="timeline-content"
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: index * 0.2 + 0.3 }}
              >
                <div className="timeline-header">
                  <h3>{item.quarter} - {item.title}</h3>
                  <span className={`status-badge ${item.status}`}>
                    {item.status === 'completed' && '✓ Completed'}
                    {item.status === 'active' && '🚀 In Progress'}
                    {item.status === 'upcoming' && '📅 Upcoming'}
                    {item.status === 'planned' && '🔮 Planned'}
                  </span>
                </div>
                
                <ul className="timeline-list">
                  {item.items.map((listItem, itemIndex) => (
                    <motion.li 
                      key={itemIndex}
                      initial={{ opacity: 0, x: -20 }}
                      animate={inView ? { opacity: 1, x: 0 } : {}}
                      transition={{ delay: index * 0.2 + itemIndex * 0.1 + 0.5 }}
                    >
                      {listItem}
                    </motion.li>
                  ))}
                </ul>

                <div className="progress-bar-container">
                  <motion.div 
                    className="progress-bar"
                    initial={{ width: 0 }}
                    animate={inView ? { 
                      width: item.status === 'completed' ? '100%' : 
                             item.status === 'active' ? '60%' : '0%'
                    } : {}}
                    transition={{ duration: 1, delay: index * 0.2 + 0.8 }}
                  />
                </div>
              </motion.div>

              <div className="timeline-glow"></div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div 
          className="roadmap-stats"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1.5 }}
        >
          <div className="stat-item">
            <div className="stat-icon">🎯</div>
            <div className="stat-content">
              <div className="stat-number">25+</div>
              <div className="stat-label">Milestones</div>
            </div>
          </div>
          <div className="stat-item">
            <div className="stat-icon">⚡</div>
            <div className="stat-content">
              <div className="stat-number">60%</div>
              <div className="stat-label">Progress</div>
            </div>
          </div>
          <div className="stat-item">
            <div className="stat-icon">🚀</div>
            <div className="stat-content">
              <div className="stat-number">5</div>
              <div className="stat-label">Quarters</div>
            </div>
          </div>
        </motion.div>
      </div>

      <div className="roadmap-bg">
        <div className="timeline-line">
          <motion.div 
            className="progress-line"
            initial={{ height: 0 }}
            animate={inView ? { height: "60%" } : {}}
            transition={{ duration: 2, delay: 0.5 }}
          />
        </div>
        
        <div className="floating-elements">
          {[...Array(5)].map((_, i) => (
            <motion.div
              key={i}
              className={`floating-element element-${i + 1}`}
              animate={{
                y: [0, -20, 0],
                rotate: [0, 180, 360],
                opacity: [0.5, 1, 0.5]
              }}
              transition={{
                duration: 8 + i,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Roadmap;